<template>
  <div v-if="equipment && Object.keys(equipment).length" class="space-y-4">

    <!-- ═══ PRICING CARD ═══ -->
    <div class="bg-theme-card rounded-2xl border border-theme-border shadow-lg p-5">
      <!-- Name + Rating row -->
      <div class="mb-3">
        <h1 class="text-2xl font-extrabold text-theme-text tracking-tight leading-tight mb-1">
          {{ equipment.name }}
        </h1>
        <div class="flex items-center gap-2">
          <div class="flex items-center gap-0.5">
            <template v-for="star in 5" :key="star">
              <i :class="[
                'pi text-sm',
                star <= Math.floor(equipment.rating || 0)
                  ? 'pi-star-fill text-yellow-400'
                  : star - 0.5 <= (equipment.rating || 0)
                    ? 'pi-star-fill text-yellow-400/50'
                    : 'pi-star text-neutral-300'
              ]"></i>
            </template>
          </div>
          <span class="text-sm text-theme-muted">
            {{ (equipment.rating || 0).toFixed(1) }}
            <span class="text-theme-text2">({{ equipment.equipment_reviews?.length || 0 }})</span>
          </span>
          <span v-if="equipment.is_trending"
            class="ml-auto inline-flex items-center gap-1 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-semibold px-2 py-0.5 rounded-full">
            <i class="pi pi-bolt text-[10px]"></i> Trending
          </span>
        </div>
      </div>

      <!-- Rate tier toggle -->
      <div class="mb-4">
        <div class="flex bg-theme-bg2 rounded-xl p-1 gap-1">
          <button v-for="tier in rateTiers" :key="tier.key"
            @click="selectedTier = tier.key"
            class="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
            :class="selectedTier === tier.key
              ? 'bg-primary-600 text-white shadow-md'
              : 'text-theme-text2 hover:text-theme-text hover:bg-theme-card'">
            {{ tier.label }}
          </button>
        </div>
        <div class="mt-3 text-center">
          <span class="text-3xl font-extrabold text-primary-600">
            {{ formatPrice(currentRate) }}
          </span>
          <span class="text-base text-theme-muted ml-1">/ {{ selectedTier }}</span>
        </div>
        <p v-if="selectedTier !== 'day'" class="text-center text-xs text-green-600 mt-0.5">
          Save {{ savingsPercent }}% vs daily rate
        </p>
      </div>

      <!-- Availability badge -->
      <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl"
        :class="equipment.is_available ? 'bg-green-50 dark:bg-green-500/10' : 'bg-red-50 dark:bg-red-500/10'">
        <div class="w-2 h-2 rounded-full" :class="equipment.is_available ? 'bg-green-500' : 'bg-red-500'"></div>
        <span class="text-sm font-medium" :class="equipment.is_available ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'">
          {{ equipment.is_available ? `${equipment.available_quantity} Available` : 'Currently Unavailable' }}
        </span>
      </div>

      <!-- Booking form -->
      <div v-if="authStore.user?.role !== 'lessor' && equipment.owner?.id !== authStore.user?.id && equipment.is_available">
        <form @submit.prevent="submitBooking" novalidate class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-theme-text2 mb-1">Start date</label>
              <input type="date" v-model="startDate" :min="today"
                class="w-full px-3 py-2.5 bg-theme-bg2 border border-theme-border rounded-xl text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition"
                @input="() => { updateDynamicAvailability(); dateError = '' }" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-theme-text2 mb-1">End date</label>
              <input type="date" v-model="endDate" :min="startDate || today"
                class="w-full px-3 py-2.5 bg-theme-bg2 border border-theme-border rounded-xl text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition"
                @input="() => { updateDynamicAvailability(); dateError = '' }" />
            </div>
          </div>
          <p v-if="dateError" class="text-xs text-red-600">{{ dateError }}</p>

          <div>
            <label class="block text-xs font-semibold text-theme-text2 mb-1">Quantity</label>
            <input type="number" v-model="quantity" min="1" :max="equipment.available_quantity"
              class="w-full px-3 py-2.5 bg-theme-bg2 border border-theme-border rounded-xl text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition"
              @input="quantityError = ''" />
            <p v-if="quantityError" class="text-xs text-red-600 mt-0.5">{{ quantityError }}</p>
          </div>

          <!-- Dynamic availability -->
          <p v-if="dynamicAvailability" class="text-sm font-semibold"
            :class="dynamicAvailability.includes('✅') ? 'text-green-600' : 'text-red-600'">
            {{ dynamicAvailability }}
          </p>

          <!-- Total estimate -->
          <div v-if="rentalDays > 0" class="flex items-center justify-between bg-theme-bg2 rounded-xl px-4 py-3">
            <span class="text-sm text-theme-muted">{{ rentalDays }} day{{ rentalDays > 1 ? 's' : '' }} × {{ quantity }} item{{ quantity > 1 ? 's' : '' }}</span>
            <span class="text-lg font-bold text-theme-text">{{ formatPrice(estimatedTotal) }}</span>
          </div>

          <button type="submit"
            :disabled="isFullyBooked || equipment.available_quantity <= 0"
            class="w-full py-3 rounded-xl font-bold text-base transition-all active:scale-[0.98]"
            :class="isFullyBooked || equipment.available_quantity <= 0
              ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-600/20'">
            <i class="pi pi-shopping-cart mr-2"></i>
            Add to Cart
          </button>
        </form>
      </div>

      <!-- Non-available message for owner's own item -->
      <div v-else-if="equipment.owner?.id === authStore.user?.id"
        class="text-center py-3 text-sm text-theme-muted">
        <i class="pi pi-info-circle mr-1"></i> This is your listing
      </div>
    </div>

    <!-- ═══ OWNER CARD ═══ -->
    <div class="bg-theme-card rounded-2xl border border-theme-border p-4">
      <div class="flex items-center gap-3">
        <div class="relative">
          <div class="w-12 h-12 rounded-full overflow-hidden bg-theme-bg2 border-2 border-theme-border">
            <img v-if="equipment.owner?.image_url" :src="equipment.owner.image_url"
              :alt="equipment.owner.first_name" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full flex items-center justify-center text-theme-muted text-lg font-bold">
              {{ (equipment.owner?.first_name || '?')[0] }}
            </div>
          </div>
          <div v-if="equipment.owner?.is_verified"
            class="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-theme-card">
            <i class="pi pi-check text-white text-[8px]"></i>
          </div>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-theme-text truncate">
            {{ equipment.owner?.first_name }} {{ equipment.owner?.last_name }}
          </p>
          <p v-if="equipment.owner?.company_name" class="text-xs text-theme-muted truncate">
            {{ equipment.owner.company_name }}
          </p>
        </div>
        <button
          v-if="authStore.isAuthenticated && equipment.owner?.id !== authStore.user?.id"
          @click="createChat"
          class="shrink-0 px-4 py-2 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-xl text-sm font-semibold transition">
          <i class="pi pi-comments mr-1.5"></i> Chat
        </button>
      </div>
    </div>

    <!-- ═══ LOCATION CARD ═══ -->
    <div class="bg-theme-card rounded-2xl border border-theme-border p-4 space-y-3">
      <h3 class="text-sm font-semibold text-theme-text flex items-center gap-2">
        <i class="pi pi-map-marker text-primary-600"></i> Location
      </h3>

      <div v-if="equipment.address" class="text-sm text-theme-text2 space-y-0.5">
        <p v-if="equipment.address.street_address">{{ equipment.address.street_address }}</p>
        <p>
          {{ equipment.address.city }}
          <span v-if="equipment.address.state">, {{ equipment.address.state }}</span>
          <span v-if="equipment.address.zip_code"> {{ equipment.address.zip_code }}</span>
        </p>
        <p v-if="equipment.address.country">{{ equipment.address.country }}</p>
      </div>
      <p v-else class="text-sm text-theme-muted">No location provided</p>

      <!-- Distance -->
      <div class="flex items-center gap-3 py-2 px-3 bg-theme-bg2 rounded-xl">
        <div class="relative w-10 h-10 flex items-center justify-center shrink-0">
          <div class="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-md">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 2a5 5 0 00-5 5c0 3.53 4.02 8.16 4.27 8.45a1 1 0 001.46 0C11 15.16 15 10.53 15 7a5 5 0 00-5-5zm0 7a2 2 0 110-4 2 2 0 010 4z" clip-rule="evenodd"/>
            </svg>
          </div>
          <span class="absolute inset-0 rounded-full border-2 border-primary-400/40 animate-ping"></span>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-xs text-theme-muted">Distance</p>
          <p class="text-sm font-semibold" :class="distanceError ? 'text-red-600' : 'text-theme-text'">
            {{ distanceError || distanceLabel || 'Unavailable' }}
          </p>
          <p v-if="!distanceLabel && !distanceError" class="text-[11px] text-theme-muted">
            Log in & set your location to see distance
          </p>
        </div>
      </div>

      <!-- Map -->
      <div class="h-40 rounded-xl overflow-hidden border border-theme-border bg-theme-bg2">
        <iframe v-if="equipmentMapUrl" :src="equipmentMapUrl"
          width="100%" height="100%" style="border:0" allowfullscreen loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"></iframe>
        <div v-else class="w-full h-full flex items-center justify-center text-sm text-theme-muted">
          <i class="pi pi-map mr-2"></i> Map not available
        </div>
      </div>
    </div>

    <!-- ═══ TAGS ═══ -->
    <div v-if="equipment.tags?.length" class="bg-theme-card rounded-2xl border border-theme-border p-4">
      <h3 class="text-sm font-semibold text-theme-text mb-2">Tags</h3>
      <div class="flex flex-wrap gap-1.5">
        <span v-for="tag in equipment.tags" :key="tag.name"
          class="bg-primary-600/10 text-primary-600 px-3 py-1 rounded-full text-xs font-semibold">
          {{ tag.name }}
        </span>
      </div>
    </div>
  </div>

  <!-- ═══ MESSAGE MODAL ═══ -->
  <div v-if="chatStore.reviewModalVisible"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    @click.self="chatStore.closeReviewModal()">
    <div class="w-full max-w-lg rounded-2xl bg-theme-card shadow-2xl border border-theme-border">
      <div class="p-4 border-b border-theme-border flex items-center justify-between">
        <h3 class="text-base font-bold text-theme-text">Message Owner</h3>
        <button @click="chatStore.closeReviewModal()" class="text-theme-muted hover:text-theme-text transition">
          <i class="pi pi-times"></i>
        </button>
      </div>
      <div class="p-4">
        <div class="flex items-start gap-3 mb-4">
          <div class="w-14 h-14 rounded-xl overflow-hidden border border-theme-border bg-theme-bg2 shrink-0">
            <img v-if="chatStore.chatState?.equipmentImage" :src="chatStore.chatState.equipmentImage"
              alt="Equipment" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full flex items-center justify-center text-theme-muted">
              <i class="pi pi-image"></i>
            </div>
          </div>
          <div>
            <p class="text-xs text-theme-muted">About</p>
            <p class="text-sm font-semibold text-theme-text">{{ equipment.name }}</p>
          </div>
        </div>
        <textarea v-model="chatStore.chatState.initialMessage" rows="4"
          class="w-full p-3 bg-theme-bg2 border border-theme-border rounded-xl text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
          placeholder="Type your message..."></textarea>
        <p v-if="modalError" class="text-xs text-red-600 mt-2">{{ modalError }}</p>
      </div>
      <div class="p-4 border-t border-theme-border flex items-center justify-end gap-2">
        <button @click="chatStore.closeReviewModal()" :disabled="sendingInitial"
          class="px-4 py-2.5 rounded-xl border border-theme-border text-sm font-semibold text-theme-text2 hover:bg-theme-bg2 transition">
          Cancel
        </button>
        <button @click="sendInitialFromModal" :disabled="sendingInitial"
          class="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60 transition">
          {{ sendingInitial ? 'Sending...' : 'Send Message' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useCartStore } from '@/store/cart'
import useNotifications from '@/store/notification'
import { useChatStore } from '@/store/chat'
import { useCurrency } from '@/composables/useCurrency'

const { formatPrice } = useCurrency()

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const chatStore = useChatStore()
const cartStore = useCartStore()
const { showNotification } = useNotifications()

const api_base_url = import.meta.env.VITE_API_BASE_URL
const equipmentId = route.params.id

const props = defineProps({
  equipment: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
})

const equipment = computed(() => props.equipment || {})

// ── Rate tiers ─────────────────────────────────────────────────────────
const selectedTier = ref('day')
const rateTiers = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
]

const currentRate = computed(() => {
  const daily = equipment.value?.hourly_rate || 0
  if (selectedTier.value === 'week') return equipment.value?.weekly_rate || daily * 7 * 0.9
  if (selectedTier.value === 'month') return equipment.value?.monthly_rate || daily * 30 * 0.8
  return daily
})

const savingsPercent = computed(() => {
  if (selectedTier.value === 'week') return 10
  if (selectedTier.value === 'month') return 20
  return 0
})

// ── Booking state ──────────────────────────────────────────────────────
const startDate = ref('')
const endDate = ref('')
const quantity = ref(1)
const dateError = ref('')
const quantityError = ref('')
const totalBooked = ref(0)
const items_data = ref(null)
const dynamicAvailability = ref('')
const today = new Date().toISOString().split('T')[0]

const isFullyBooked = computed(() => props.equipment?.available_quantity === 0)

const rentalDays = computed(() => {
  if (!startDate.value || !endDate.value) return 0
  const diff = new Date(endDate.value) - new Date(startDate.value)
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
})

const estimatedTotal = computed(() => {
  const daily = equipment.value?.hourly_rate || 0
  return daily * rentalDays.value * (quantity.value || 1)
})

// ── Modal send ─────────────────────────────────────────────────────────
const sendingInitial = ref(false)
const modalError = ref('')

const sendInitialFromModal = async () => {
  modalError.value = ''
  const msg = (chatStore.chatState?.initialMessage || '').toString().trim()
  if (!msg) { modalError.value = 'Please type a message before sending.'; return }
  try {
    sendingInitial.value = true
    await chatStore.sendMessageAndReset()
    sendingInitial.value = false
    router.push({ path: '/new-profile/chats' })
  } catch {
    sendingInitial.value = false
    modalError.value = 'Failed to send message. Please try again.'
  }
}

// ── Distance ───────────────────────────────────────────────────────────
const distanceKm = ref(null)
const distanceError = ref(null)

const distanceLabel = computed(() => {
  if (distanceKm.value === null || distanceKm.value === undefined) return ''
  const numKm = Number(distanceKm.value)
  if (isNaN(numKm)) return ''
  const miles = numKm * 0.621371
  if (miles < 1) return `${miles.toFixed(2)} miles away`
  if (miles < 50) return `${miles.toFixed(1)} miles away`
  return `${Math.round(miles)} miles away`
})

const fetchDistance = async () => {
  distanceError.value = null
  distanceKm.value = null
  if (!authStore.isAuthenticated) {
    distanceError.value = 'Log in to see distance'
    return
  }
  try {
    const response = await axios.get(`${api_base_url}/api/locations/check-distance/`, {
      params: { equipment_id: equipmentId }, withCredentials: true,
    })
    distanceKm.value = response.data.distance_km
  } catch (error) {
    const backend = error.response?.data
    let message = 'Distance unavailable'
    if (backend) {
      if (typeof backend === 'string') message = backend
      else if (backend.detail) message = backend.detail
      else if (backend.error) message = backend.error
      else if (backend.message) message = backend.message
    }
    distanceError.value = message
  }
}

// ── Map URL ────────────────────────────────────────────────────────────
const equipmentMapUrl = computed(() => {
  const addr = equipment.value?.address
  if (!addr) return ''
  if (addr.latitude != null && addr.longitude != null) {
    const lat = Number(addr.latitude), lng = Number(addr.longitude)
    if (!isNaN(lat) && !isNaN(lng)) return `https://www.google.com/maps?q=${lat},${lng}&z=14&output=embed`
  }
  if (addr.formatted_address) return `https://www.google.com/maps?q=${encodeURIComponent(addr.formatted_address)}&z=14&output=embed`
  const parts = [addr.street_address, addr.city, addr.state, addr.zip_code, addr.country].filter(Boolean)
  return parts.length ? `https://www.google.com/maps?q=${encodeURIComponent(parts.join(', '))}&z=14&output=embed` : ''
})

// ── Helpers ────────────────────────────────────────────────────────────
const formatDate = (date) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const createChat = async () => {
  if (!props.equipment?.owner?.id) return
  const chatId = await chatStore.createChat(props.equipment.owner.id, props.equipment)
  if (chatId) {
    const draft = `Hello, I am interested in renting the ${props.equipment.name}. Could you please provide more details?`
    const itemImg = props.equipment.images?.[0]?.image_url || ''
    const itemName = props.equipment.name || ''
    router.push({ name: 'Chats', query: { open: chatId, draft, itemImg, itemName } })
  }
}

const updateDynamicAvailability = () => {
  if (!startDate.value || !endDate.value || !items_data.value?.length) { dynamicAvailability.value = ''; return }
  let overlapQty = 0
  const s = new Date(startDate.value), e = new Date(endDate.value)
  items_data.value.forEach(b => {
    if (s <= new Date(b.end_date) && e >= new Date(b.start_date)) overlapQty += b.quantity
  })
  const available = Math.max(0, props.equipment.available_quantity - overlapQty)
  dynamicAvailability.value = available > 0
    ? `✅ ${available} available for ${formatDate(startDate.value)} → ${formatDate(endDate.value)}`
    : `❌ Fully booked for ${formatDate(startDate.value)} → ${formatDate(endDate.value)}`
}

const validateDates = () => {
  dateError.value = ''
  if (!startDate.value || !endDate.value) { dateError.value = 'Select both dates.'; return false }
  const todayObj = new Date(); todayObj.setHours(0,0,0,0)
  if (new Date(startDate.value) < todayObj) { dateError.value = 'Start date cannot be in the past.'; return false }
  if (new Date(startDate.value) > new Date(endDate.value)) { dateError.value = 'End must be after start.'; return false }
  return true
}

const validateQuantity = () => {
  quantityError.value = ''
  const qty = parseInt(quantity.value, 10)
  if (!qty || isNaN(qty) || qty <= 0) { quantityError.value = 'Enter a valid quantity.'; return false }
  if (qty > props.equipment.available_quantity) { quantityError.value = `Only ${props.equipment.available_quantity} available.`; return false }
  return true
}

const fetchTotalBookedItems = async () => {
  try {
    const response = await axios.get(`${api_base_url}/api/order-items/${equipmentId}/total-booked/`, { withCredentials: true })
    totalBooked.value = response.data.total_booked
    items_data.value = response.data.booked_dates
  } catch {}
}

const submitBooking = async () => {
  dateError.value = ''; quantityError.value = ''
  if (!validateDates() || !validateQuantity()) return
  const payload = { item: props.equipment.id, quantity: parseInt(quantity.value, 10), start_date: startDate.value, end_date: endDate.value }

  if (authStore.isAuthenticated) {
    try {
      await axios.post(`${api_base_url}/api/cart-items/`, payload, { withCredentials: true })
      await cartStore.loadCart()
      showNotification('Added', 'Item added to cart!', 'success')
      await fetchTotalBookedItems()
    } catch (error) {
      const backend = error.response?.data || {}
      if (backend.quantity) quantityError.value = backend.quantity[0]
      else if (backend.non_field_errors) quantityError.value = backend.non_field_errors[0]
      else if (backend.detail) dateError.value = backend.detail
      else showNotification('Error', 'Booking failed', 'error')
    }
  } else {
    await cartStore.addItemToCart({
      equipment: equipment.value, quantity: payload.quantity,
      start_date: payload.start_date, end_date: payload.end_date,
    })
  }
}

onMounted(async () => {
  await fetchTotalBookedItems()
  await fetchDistance()
})

watch(() => props.equipment, async () => { await fetchDistance() })
</script>
